import type { PaymentFieldConfig, PaymentMethod } from './types';

/** What the supporter has chosen on a payment field. */
export interface PaymentSelection {
  amountMinor?: number;
  frequency?: 'one_off' | 'recurring';
  method?: PaymentMethod;
  /**
   * The gift handle the amount came from, when it came from one. Sent with the
   * rest of the form's values, so it is stored on the submission; nothing maps
   * it into the CRM yet. The amount charged is `amountMinor`, never this.
   */
  giftHandleId?: string;
}

/** The single method assumed for any field that does not name its own. */
export const DEFAULT_PAYMENT_METHODS: readonly PaymentMethod[] = ['card'];

/**
 * The methods a field offers, defaulted and de-duplicated.
 *
 * The renderer NEVER rejects a combination — the server owns that (see
 * `resolvePaymentMethods` in Engage/backend/shared/payment-field.ts, which
 * throws where this shrugs). A supporter must not meet a broken screen because
 * an author saved something odd: show the usable subset and let the endpoint
 * refuse the rest.
 */
export function resolveOfferedMethods(config: PaymentFieldConfig): PaymentMethod[] {
  const configured = config.methods;
  if (!Array.isArray(configured) || configured.length === 0) return [...DEFAULT_PAYMENT_METHODS];
  const known = configured.filter((m): m is PaymentMethod => m === 'card' || m === 'direct_debit');
  if (known.length === 0) return [...DEFAULT_PAYMENT_METHODS];
  return [...new Set(known)];
}

/**
 * The frequency THIS submission resolves to — the field's own, unless the field
 * lets the supporter pick. Mirrors the server's `resolvePaymentAmount`, and is
 * shared so the field and the submit handler cannot disagree about it.
 */
export function resolveFrequency(
  config: PaymentFieldConfig,
  selection: PaymentSelection,
): 'one_off' | 'recurring' {
  if (config.frequency === 'donor_choice') return selection.frequency === 'recurring' ? 'recurring' : 'one_off';
  return config.frequency === 'recurring' ? 'recurring' : 'one_off';
}

/**
 * The methods usable for this selection, which is narrower than what the field
 * offers. A `donor_choice` field may legitimately offer Direct Debit — "give
 * once or monthly" is exactly the ask a charity wants it on — but a one-off
 * Direct Debit is a different GoCardless product, and the sign-up endpoint
 * refuses it with `DIRECT_DEBIT_REQUIRES_RECURRING`. Hide the option while the
 * supporter is on one-off rather than letting them pick a dead end.
 */
export function availableMethods(config: PaymentFieldConfig, selection: PaymentSelection): PaymentMethod[] {
  const frequency = resolveFrequency(config, selection);
  return resolveOfferedMethods(config).filter((m) => m !== 'direct_debit' || frequency === 'recurring');
}

/**
 * The method this submission will use. A supporter who never touched the choice
 * (or whose choice has since become unavailable, e.g. by switching to one-off)
 * gets the first offered method, so the submit handler never has to guess.
 */
export function resolveSelectedMethod(config: PaymentFieldConfig, selection: PaymentSelection): PaymentMethod {
  const available = availableMethods(config, selection);
  if (selection.method && available.includes(selection.method)) return selection.method;
  return available[0] ?? 'card';
}
